import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';

interface UnsavedChangesBarProps {
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  handleSave: () => void;
  isSaving: boolean;
  colors: any;
  isDark: boolean;
  lokiGradient?: string;
  goldColor?: string;
}

const UnsavedChangesBar: React.FC<UnsavedChangesBarProps> = ({
  isEditing,
  setIsEditing,
  handleSave,
  isSaving,
  colors,
  isDark,
  lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
  goldColor = '#C4B66F',
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  if (!isEditing) return null;

  return (
    <Box sx={{ 
      position: 'fixed', 
      bottom: isMobile ? 24 : 20, // Raised up on mobile
      right: isMobile ? 16 : { xs: 20, md: 40 }, 
      left: isMobile ? 16 : { xs: 20, md: 320 },
      bgcolor: isMobile 
        ? (isDark ? alpha(colors.sidebarBg, 0.98) : alpha('#ffffff', 0.98)) 
        : (isDark ? alpha(colors.sidebarBg, 0.95) : alpha('#ffffff', 0.95)), 
      p: isMobile ? 1.5 : 1.5, // Thicker on mobile
      px: isMobile ? 3 : 3, 
      borderRadius: isMobile ? '14px' : '16px', // Slightly larger border radius on mobile
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      boxShadow: isMobile 
        ? '0 12px 32px rgba(0,0,0,0.6)' // Stronger shadow on mobile
        : '0 8px 24px rgba(0,0,0,0.5)',
      zIndex: 1000,
      border: `1px solid ${colors.borderColor}`,
      maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none',
      backdropFilter: isMobile 
        ? (isDark ? 'blur(30px)' : 'blur(20px)') // More blur on mobile dark mode
        : 'blur(20px)',
      WebkitBackdropFilter: isMobile 
        ? (isDark ? 'blur(30px)' : 'blur(20px)') // More blur on mobile dark mode
        : 'blur(20px)',
      height: isMobile ? 'auto' : 'auto',
      minHeight: isMobile ? '56px' : 'auto', // Minimum height for mobile
    }}>
      {/* Warning text - bigger font on mobile */}
      <Typography 
        color={colors.textPrimary} 
        sx={{ 
          fontWeight: isMobile ? 700 : 600, // Bolder on mobile
          fontSize: isMobile ? '0.95rem' : '0.9rem', // Bigger font on mobile
          textAlign: 'left',
          flex: 1,
          mr: isMobile ? 3 : 2, // More margin on mobile
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block'
        }}
      >
        {isMobile ? 'Unsaved changes!' : 'Careful — you have unsaved changes!'}
      </Typography>

      {/* Buttons container - bigger buttons on mobile */}
      <Box sx={{ 
        display: 'flex', 
        gap: isMobile ? 1.5 : 2, // More gap on mobile
        flexShrink: 0
      }}>
        <Button 
          color="inherit" 
          onClick={() => setIsEditing(false)} 
          sx={{ 
            borderRadius: '20px', 
            textTransform: 'none', 
            fontWeight: isMobile ? 700 : 600, // Bolder on mobile
            color: colors.textPrimary,
            fontSize: isMobile ? '0.9rem' : '0.85rem', // Bigger font on mobile
            px: isMobile ? 2.5 : 2, // More padding on mobile
            py: isMobile ? 0.75 : 0.625, // More padding on mobile
            minWidth: isMobile ? '75px' : '80px', // Bigger on mobile
            height: isMobile ? '38px' : 'auto', // Fixed height on mobile
            border: `1px solid ${alpha(colors.textPrimary, 0.3)}`, // Stronger border on mobile
            '&:hover': {
              bgcolor: alpha(colors.textPrimary, 0.1) // Stronger hover on mobile
            }
          }}
        >
          Reset
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={isSaving}
          sx={{ 
            borderRadius: '20px', 
            fontWeight: isMobile ? 700 : 600, // Bolder on mobile
            textTransform: 'none', 
            background: lokiGradient,
            color: goldColor, 
            px: isMobile ? 3 : 2.5, // More padding on mobile
            py: isMobile ? 0.75 : 0.625, // More padding on mobile
            boxShadow: isMobile 
              ? '0 6px 20px rgba(0,0,0,0.4)' // Stronger shadow on mobile
              : '0 4px 12px rgba(0,0,0,0.3)',
            border: `1px solid ${alpha(goldColor, isMobile ? 0.4 : 0.3)}`, // Stronger border on mobile
            fontSize: isMobile ? '0.9rem' : '0.85rem', // Bigger font on mobile
            minWidth: isMobile ? '85px' : '120px', // Bigger on mobile
            height: isMobile ? '38px' : 'auto', // Fixed height on mobile
            '&:hover': {
              boxShadow: isMobile 
                ? '0 8px 24px rgba(0,0,0,0.5)' // Stronger hover shadow on mobile
                : '0 6px 16px rgba(0,0,0,0.4)',
              background: lokiGradient,
              opacity: 0.92
            },
            '&:disabled': {
              background: isDark ? alpha('#0F3B2E', 0.6) : alpha('#0F3B2E', 0.4),
              color: alpha(goldColor, 0.6),
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              boxShadow: 'none'
            }
          }}
        >
          {isSaving ? (
            <CircularProgress 
              size={isMobile ? 20 : 20} // Slightly bigger on mobile
              sx={{ color: goldColor }} 
            />
          ) : (
            isMobile ? 'Save' : 'Save Changes'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default UnsavedChangesBar;